import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from 'lib/queryClient'
import {
  fetchOnboardingData,
  fetchTeamPreferencesStatus,
  handleStepAction,
  updateStepCompletion,
  UserData,
} from 'services/onboardingApi'

import * as snugNotifier from 'app/services/snugNotifier'

// TypeScript interfaces for hook parameters
interface UpdateStepCompletionVariables {
  stepId: string
  completed: boolean
}

interface StepActionVariables {
  stepId: string
  actionType: string
}

/**
 * Hook to fetch onboarding data (user info and steps)
 */
export const useOnboardingData = (
  teamGUID: string,
  userData: UserData = {},
) => {
  return useQuery({
    queryKey: queryKeys.onboarding.data(teamGUID),
    queryFn: () => fetchOnboardingData(teamGUID, userData),
    enabled: !!teamGUID, // Only run query if teamGUID is available
    select: (data) => {
      // Transform API data if needed
      return {
        ...data,
        // Add any additional computed properties
        progressPercentage:
          data.steps.length > 0
            ? Math.round((data.completedStepsCount / data.steps.length) * 100)
            : 0,
      }
    },
  })
}

/**
 * Hook to update step completion status (optimistic update)
 */
export const useUpdateStepCompletion = (teamGUID: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: UpdateStepCompletionVariables) =>
      updateStepCompletion(teamGUID, variables.stepId, variables.completed),
    // Optimistic update for better UX
    onMutate: async (variables: UpdateStepCompletionVariables) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({
        queryKey: queryKeys.onboarding.data(teamGUID),
      })

      // Snapshot the previous value
      const previousData = queryClient.getQueryData(
        queryKeys.onboarding.data(teamGUID),
      )

      // Optimistically update to the new value
      queryClient.setQueryData(
        queryKeys.onboarding.data(teamGUID),
        (old: any) => {
          if (!old) return old

          const updatedSteps = old.steps.map((step: any) =>
            step.id === variables.stepId
              ? { ...step, completed: variables.completed }
              : step,
          )

          const completedStepsCount = updatedSteps.filter(
            (step: any) => step.completed,
          ).length

          return {
            ...old,
            steps: updatedSteps,
            completedStepsCount,
            progressPercentage: Math.round(
              (completedStepsCount / updatedSteps.length) * 100,
            ),
          }
        },
      )

      // Return a context object with the snapshotted value
      return { previousData }
    },
    onError: (
      error: unknown,
      _variables: UpdateStepCompletionVariables,
      context: any,
    ) => {
      // Revert the optimistic update on error
      if (context?.previousData) {
        queryClient.setQueryData(
          queryKeys.onboarding.data(teamGUID),
          context.previousData,
        )
      }

      const apiError = error as any
      snugNotifier.error(
        `Failed to update step: ${apiError?.message || 'Unknown error'}`,
      )
    },
    onSuccess: (_data: any, _variables: UpdateStepCompletionVariables) => {
      // Refetch to ensure data consistency
      queryClient.invalidateQueries({
        queryKey: queryKeys.onboarding.data(teamGUID),
      })
    },
  })
}

/**
 * Hook to handle step actions (videos, forms, etc.)
 */
export const useStepAction = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (variables: StepActionVariables) =>
      handleStepAction(variables.stepId, variables.actionType),
    onSuccess: (_data: any, variables: StepActionVariables) => {
      // Show success notification based on action type
      let message = 'Action completed successfully'

      switch (variables.actionType) {
        case 'video':
          message = 'Tutorial opened successfully'
          break
        case 'primary':
          message = 'Form action completed'
          break
        case 'book':
          message = 'Setup call booking initiated'
          break
        default:
          message = 'Action completed successfully'
      }

      snugNotifier.success(message)

      // Optionally invalidate queries if action affects step state
      if (
        variables.actionType === 'primary' ||
        variables.actionType === 'book'
      ) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.onboarding.data(''),
        })
      }
    },
    onError: (error: unknown, variables: StepActionVariables) => {
      // Error notification is handled by QueryClient default config
      console.error(
        `Failed to handle action ${variables.actionType} for step ${variables.stepId}:`,
        error,
      )
    },
  })
}

/**
 * Hook to fetch team preferences status
 */
export const useTeamPreferencesStatus = (agencyGUID: string) => {
  return useQuery({
    queryKey: queryKeys.onboarding.preferences(agencyGUID),
    queryFn: () => fetchTeamPreferencesStatus(agencyGUID),
    enabled: !!agencyGUID, // Only run query if agencyGUID is available
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    select: (data: any) => {
      // Transform API data into a more usable format
      const preferencesMap: Record<string, any> = {}

      if (data.preferences) {
        data.preferences.forEach((pref: any) => {
          preferencesMap[pref.name] = {
            status: pref.currentStatus,
            message: pref.message,
            description: pref.description,
          }
        })
      }

      return {
        preferences: preferencesMap,
        rawData: data.preferences || [],
        meta: data.meta,
        errors: data.errors,
      }
    },
  })
}
